/**
 * Helpers for resolving the element a URL hash points at.
 *
 * Anchors on this site are not always CSS-safe identifiers — ACE rule sections use ids like
 * `acerule=eligibility_for_the_rosa&part=categories`. `getElementById` is used deliberately
 * because it does not parse its argument as a selector, unlike `querySelector('#...')`.
 */

/**
 * Carries the id a rich-text heading had before headings inside accordions were prefixed with
 * the accordion title (OC2-8078), e.g. `marking-guidelines` for `performance-marking-guidelines`.
 */
export const LEGACY_HEADING_ANCHOR_ATTRIBUTE = 'data-heading-anchor'

export const getHashTargetId = (): string => {
	if (typeof window === 'undefined') return ''

	const rawHash = window.location.hash.replace('#', '')
	if (!rawHash) return ''

	try {
		return decodeURIComponent(rawHash)
	} catch {
		// Malformed percent-encoding — fall back to the raw value
		return rawHash
	}
}

export const findHashTargetElement = (): HTMLElement | null => {
	const id = getHashTargetId()
	if (!id) return null

	// Links shared before the prefix existed still land where they used to: the first heading
	// that had this id
	return (
		document.getElementById(id) ||
		document.querySelector<HTMLElement>(
			`[${LEGACY_HEADING_ANCHOR_ATTRIBUTE}="${CSS.escape(id)}"]`,
		)
	)
}

/**
 * Whether the element is actually laid out where the reader can see it.
 *
 * This gates the scroll, so it has to be true only once the accordion has really opened.
 * The three checks catch different collapse mechanics:
 *
 * - `offsetParent` is null when an ancestor is `display: none` — the NSW Design System puts
 *   `hidden` on its accordion content panel.
 * - `visibility` is the one that matters for MUI: a collapsed `Collapse` keeps zero height but
 *   still reports a non-null `offsetParent` AND a client rect, so without this check an anchor
 *   inside a closed MUI accordion looks ready and we scroll to its pre-expansion position.
 *   `visibility` inherits, so reading it off the element itself catches a hidden ancestor.
 * - client rects cover `position: fixed` subtrees, where `offsetParent` is always null.
 */
export const isElementRendered = (el: HTMLElement): boolean => {
	if (!el.isConnected) return false
	if (getComputedStyle(el).visibility === 'hidden') return false

	return el.offsetParent !== null || el.getClientRects().length > 0
}
